<template>
  <div class="leaflet-container">
    <Breadcrumb class="d-none d-sm-flex px-0" :items="vBreadcrums" />
    <v-container class="upper">
      <v-row class="d-flex flex-row" v-if="leaflet">
        <v-col
          :cols="$vuetify.breakpoint.smAndUp ? 8 : 12"
          class="d-flex justify-start flex-column"
        >
          <h2 class="secondary--text">{{ leaflet.title }}</h2>
          <p v-if="leaflet.descr">{{ leaflet.descr }}</p>
        </v-col>
        <v-col
          cols="4"
          class="d-flex justify-end"
          v-if="$vuetify.breakpoint.smAndUp"
        >
          <v-btn class="primary white--text text-uppercase" to="/volantini"
            >Torna ai volantini</v-btn
          >
        </v-col>
      </v-row>
      <div
        :class="{ iosFullscreen: isIOSFullscreen }"
        id="leafet-viewer"
        ref="leafletViewer"
        style="background-color: var(--v-grey-lighten3) !important; border-color: var(--v-grey-lighten3) !important;"
      >
        <router-view v-if="showList" />
        <leaflet-viewer
          v-else
          v-show="showLeaflet"
          :leaflet="leaflet"
          ref="leafletViewerRef"
          @openDetail="openDetail"
          @slideChanged="slideChanged"
          :key="leafletId"
          :swiperHeight="swiperHeight"
          :fullscreen="leafletFullscreen"
        />
        <v-bottom-navigation
          :class="{ 'sticky-toolbar-top': isSticky }"
          id="leaflet-toolbar"
          dense
          flat
          background-color="secondary lighten-1"
          color="primary"
          class="elevation-0"
        >
          <v-slide-group
            multiple
            show-arrows
            class="leaflet-bottom-navigation"
            :class="
              $vuetify.breakpoint.xs ? 'd-flex flex-row' : 'd-flex flex-column'
            "
          >
            <v-menu
              :top="!isSticky"
              :bottom="isSticky"
              v-show="leaflet.leafletTypeId == 1"
              offset-y
              :close-on-content-click="false"
              :value="showSearch"
              attach="#leaflet-toolbar"
              content-class="elevation-0 rounded-0"
              v-if="categories && categories.length > 0"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  depressed
                  @click="
                    showCategories = !showCategories;
                    onShowSearch();
                  "
                >
                  <span class="secondary--text"
                    ><strong>{{
                      $t("leaflet.navigation.search.title")
                    }}</strong></span
                  >
                  <v-icon color="secondary">icon-search</v-icon>
                </v-btn>
              </template>
              <div class="px-4 py-2 grey lighten-3">
                <v-text-field
                  v-model="search"
                  filled
                  solo
                  dense
                  flat
                  hide-details
                  single-line
                  height="48"
                  clearable
                  ref="flyerinput"
                  class="search-input flyer-input"
                  placeholder="Cerca un prodotto"
                  @keydown.enter.native.prevent.stop="searchProducts"
                >
                  <template slot="append">
                    <v-icon color="default" @click.prevent.stop="searchProducts"
                      >$search</v-icon
                    >
                  </template>
                </v-text-field>
              </div>
            </v-menu>
            <v-menu
              v-show="leaflet.leafletTypeId == 1"
              :top="!isSticky"
              :bottom="isSticky"
              offset-y
              attach="#leaflet-toolbar"
              content-class="rounded-0"
              v-if="categories && categories.length > 0"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  color="secondary"
                  depressed
                  @click="handleShowCategory"
                >
                  <span class="secondary--text"
                    ><strong>{{
                      $t("leaflet.navigation.departments.title")
                    }}</strong></span
                  >
                  <v-icon color="secondary">$reparti</v-icon>
                </v-btn>
              </template>
              <v-sheet class="pa-2">
                <v-row no-gutters>
                  <v-col
                    v-for="category in categories"
                    :key="category.categoryId"
                    class="pa-1"
                    cols="3"
                    sm="2"
                    md="auto"
                  >
                    <v-btn
                      depressed
                      color="secondary"
                      width="73"
                      height="73"
                      class="category-leaflet-btn"
                      @click="
                        filterProducts(category.categoryId, category.name)
                      "
                    >
                      <div
                        class="d-flex flex-column justify-space-between py-2"
                      >
                        <div class="category-block-title white--text mb-2">
                          <strong>{{ category.name }}</strong>
                        </div>
                        <div>
                          <ebsn-img
                            width="35"
                            height="35"
                            :target="category"
                            path="category_info.ICON"
                            :alt="`Icona Categoria ${category.name}`"
                          />
                        </div>
                      </div>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-sheet>
            </v-menu>
            <v-btn
              v-show="leaflet.leafletTypeId == 1"
              icon
              depressed
              @click="backToLeaflet"
            >
              <span class="secondary--text"
                ><strong>{{
                  $t("leaflet.navigation.browse.title")
                }}</strong></span
              >
              <v-icon color="secondary">$sfoglia</v-icon>
            </v-btn>

            <v-btn
              v-show="leaflet.leafletTypeId == 1"
              icon
              depressed
              @click.stop="toggleFullscreen"
            >
              <span class="secondary--text"
                ><strong>{{
                  $t("leaflet.navigation.zoom.title")
                }}</strong></span
              >
              <v-icon color="secondary">$zoom</v-icon>
            </v-btn>
            <v-btn
              v-if="leaflet"
              icon
              target="blank"
              :href="`${leaflet.baseLocation}${leaflet.location}`"
              @click="onLinkClick"
            >
              <span class="secondary--text"
                ><strong>{{
                  $t("leaflet.navigation.download.title")
                }}</strong></span
              >
              <v-icon color="secondary">icon-download-1</v-icon>
            </v-btn>
            <v-btn
              v-if="leaflet && categories && categories.length > 0"
              icon
              depressed
              @click="openleafletInfoModal"
            >
              <span class="secondary--text"
                ><strong>{{
                  $t("leaflet.navigation.info.title")
                }}</strong></span
              >
              <v-icon color="secondary">$info</v-icon>
            </v-btn>
          </v-slide-group>
          <div
            v-if="$vuetify.breakpoint.xs"
            class="d-flex flex-column fixed-text align-center secondary--text hidden-sm-and-up w-100"
          >
            Usa i comandi qui sopra per navigare il volantino
          </div>
        </v-bottom-navigation>
      </div>
    </v-container>

    <v-container v-if="!showList">
      <div class="product-grid mt-2 mt-sm-0 pb-4 pb-md-0">
        <h2 v-if="headerSearch">
          <span>{{ $t("leaflet.searchResults") }}: </span>
          <span class="font-weight-regular">{{ headerSearch }}</span>
        </h2>
        <h2 v-if="headerCategory">
          <span>{{ $t("leaflet.allOffersIn") }} </span>
          <span class="text-lowercase">{{ headerCategory }}</span>
        </h2>
        <v-row v-if="products && products.length">
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="2"
            v-for="product in products"
            :key="product.productId"
          >
            <ProductCard
              v-bind:product="product"
              :fromLeaflet="true"
              :leafletName="leaflet.name"
            />
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            {{ $t("leaflet.searchResultsNull") }}
          </v-col>
        </v-row>

        <div class="text-center mt-8" v-if="pager && pager.totPages > 1">
          <v-pagination
            v-model="currentPage"
            :length="pager.totPages"
            :total-visible="7"
            circle
            color="primary"
            @input="onPageChange"
          ></v-pagination>
        </div>
      </div>
    </v-container>
  </div>
</template>

<style lang="scss">
.extended {
  top: 120px !important;
}
.sticky-toolbar-top {
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}
.leaflet-container {
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .v-bottom-navigation {
      position: fixed;
      bottom: 14%;
      z-index: 8;
      height: calc(56px + env(safe-area-inset-top) + 20px) !important;
    }
    .fixed-text {
      position: fixed;
      bottom: 8%;
      z-index: 8;
      background-color: $white;
      padding: 16px;
      width: 100%;
      height: 56px;
      font-weight: bold;
      font-size: 13px;
      @media (max-width: 375px) {
        font-size: 11px;
        padding: 8px;
      }
    }
  }
}
.body-fixed-position {
  position: fixed;
}
.iosFullscreen {
  position: fixed !important;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  width: 100vw;
  height: 100%;
  .disable-zoom-on-safari {
    touch-action: unset !important;
  }
  .swiper-pagination,
  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
  .draggable {
    pointer-events: none;
  }
  .swiper-container {
    max-width: unset !important;
    .imgage-container,
    .swiper-zoom-container {
      max-height: unset !important;
    }
  }
}

article {
  counter-reset: my-counter;
  h1 {
    line-height: normal !important;
  }
  .leaflet-information-wrapper {
    .leaflet-information {
      flex: 1 1 33.333%;
      &.has-icon {
        max-width: fit-content;
      }
      .v-icon {
        font-size: 50px !important;
        background-color: var(--v-primary-base) !important;
        border-radius: 20px;
      }
      &-text {
        max-width: 120px;
      }
      .counter {
        position: relative;
        isolation: isolate;
        &::before {
          counter-increment: my-counter;
          content: counter(my-counter);
          position: absolute;
          z-index: -1;
          font-size: 100px;
          font-weight: bold;
          line-height: 65px;
          color: var(--v-primary-lighten2);
        }
        &-left {
          &::before {
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
        }
        &-right {
          &::before {
            right: 0;
            top: 50%;
            transform: translate(-50%, -50%);
          }
        }
      }
    }
  }
}
@media (min-width: 960px) {
  article .leaflet-information-wrapper .leaflet-information .counter {
    &-left {
      &::before {
        transform: unset;
        left: -20px;
        top: 0;
      }
    }
    &-right {
      &::before {
        transform: unset;
        right: -20px;
        top: 0;
      }
    }
  }
}
#leaflet-toolbar {
  .v-btn__content {
    color: #000;
    font-weight: 500;
    span {
      font-size: 8px;
      padding: 4px;
      text-transform: uppercase;
    }
    i {
      font-size: 20px;
    }
  }
  &.v-item-group.v-bottom-navigation .v-btn {
    min-width: 70px;
  }
  .v-menu__content {
    max-width: 100%;
    left: 0 !important;
    right: 0;
    width: 100%;
  }
}
.leaflet-container {
  .swiper-slide {
    text-align: center;
    font-size: 18px;
    background: transparent;
    display: -webkit-box;
    display: -ms-flexbox;
    display: -webkit-flex;
    display: flex;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    -webkit-justify-content: center;
    justify-content: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    -webkit-align-items: center;
    align-items: center;
  }

  .swiper-slide-active {
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    -webkit-justify-content: center;
    justify-content: center;
    -webkit-box-align: flex-end;
    -ms-flex-align: flex-end;
    -webkit-align-items: flex-end;
    align-items: flex-end;
  }
  .swiper-slide-next {
    -webkit-box-pack: flex-start;
    -ms-flex-pack: flex-start;
    -webkit-justify-content: flex-start;
    justify-content: flex-start;
    -webkit-box-align: flex-start;
    -ms-flex-align: flex-start;
    -webkit-align-items: flex-start;
    align-items: flex-start;
  }

  .swiper-slide img {
    display: block;
    max-width: 100%;
    object-fit: contain;
    max-height: inherit;
  }

  .imgage-container {
    box-sizing: content-box;
  }

  @media only screen and (min-width: 769px) {
    .swiper-slide:first-child {
      transition: transform 100ms;
    }

    .swiper-slide:first-child img {
      transition: box-shadow 500ms;
    }

    .swiper-slide.swiper-slide-active:first-child {
      transform: translateX(50%);
      z-index: 2;
    }

    .swiper-slide.swiper-slide-active:first-child img {
      box-shadow: 0px 32px 80px rgba(0, 0, 0, 0.35);
    }

    .swiper-slide:nth-child(2) {
      transition: transform 100ms;
    }

    .swiper-slide.swiper-slide-next:nth-child(2) {
      transform: translateX(55%);
      z-index: 1;
    }

    .swiper[dir="rtl"] .swiper-slide.swiper-slide-active:first-child {
      transform: translateX(-50%);
    }

    .swiper[dir="rtl"] .swiper-slide.swiper-slide-next:nth-child(2) {
      transform: translateX(-55%);
    }

    .swiper-slide-active {
      -webkit-box-pack: flex-end;
      -ms-flex-pack: flex-end;
      -webkit-justify-content: flex-end;
      justify-content: flex-end;
    }
  }

  .category-leaflet-btn {
    border-radius: 8px !important;
    .v-btn__content {
      flex-direction: column;
      .category-block-title {
        font-size: 10px;
        font-weight: normal;
        text-transform: none;
        white-space: initial;
        max-width: 70px;
      }
    }
  }
  .leaflet-page-container {
    position: relative;
    display: inline-block;
    max-height: 100%;
    max-width: 100%;
  }

  .swiper-button-next::after,
  .swiper-button-prev::after {
    content: "";
  }
}
.fullscreen {
  .leaflet-container {
    .swiper-slide img {
      height: 100% !important;
    }
    .image {
      height: 100% !important;
    }
  }
}
.leaflet-dialog-fullscreen {
  display: flex;
  align-items: center;
  .exit-fullscreen-btn {
    position: absolute;
    z-index: 2;
    right: 10px;
  }
}
.v-dialog.vuedl-layout .v-btn {
  border: 0;
}
</style>

<script>
import Breadcrumb from "@/components/navigation/Breadcrumb.vue";
import LeafletViewer from "@/components/leaflet/LeafletViewer.vue";
import ProductCard from "@/components/product/ProductCard.vue";
import Popup from "@/components/Popup.vue";
import CTADetailLeaflet from "@/components/leaflet/CTADetailLeaflet.vue";
import LeafletInformationModalEBSN from "@/components/leaflet/LeafletInformationModalEBSN.vue";
import clickHandler from "~/mixins/clickHandler";
import downloadDocument from "~/mixins/downloadDocument";
import leafletService from "~/service/leafletService";
import customService from "@/service/grosmarketCustomService";
import get from "lodash/get";
import { mapState, mapGetters } from "vuex";
import isNumber from "lodash/isNumber";
import analyticsService from "@/commons/service/analyticsService";

const header = {
  fullscreenHeader: 165,
  xsHeader: 165,
  smHeader: 0,
  desktopHeader: 40
};

export default {
  name: "Leaflet",
  components: {
    Breadcrumb,
    ProductCard,
    LeafletViewer
  },
  mixins: [clickHandler, downloadDocument],
  data() {
    return {
      dialogInstances: [],
      URLcategory: null,
      isSticky: false,
      navOffsetTop: 0,
      componentKey: 0,
      isIOSFullscreen: false,
      leafletId: null,
      areaDetail: null,
      categories: [],
      showCategories: false,
      leaflet: {},
      leafletRendered: false,
      selectedArea: null,
      products: [],
      openProductDetail: false,
      showLeaflet: true,
      showSearch: false,
      search: "",
      leafletFullscreen: false,
      headerSearch: null,
      headerCategory: null,
      currentPage: 1,
      showList: false,
      dialog: false,
      leafletSlugId: null,
      swiperHeight: this.$vuetify.breakpoint.mdAndUp
        ? window.innerHeight - header.desktopHeader + "px"
        : this.$vuetify.breakpoint.xsOnly
        ? window.innerHeight - header.xsHeader + "px"
        : window.innerHeight - header.smHeader + "px",

      // Pagination & filtering state
      pager: null,
      pageSize: 12,
      activeCategoryId: null,
      activeSearch: null,
      activeFlyerPages: null // <-- NEW: remembers which flyer page(s) are active
    };
  },
  computed: {
    ...mapState({
      allCategories: ({ category }) => category.categoryTree
    }),
    ...mapGetters({
      isAuthenticated: "cart/isAuthenticated"
    }),
    vBreadcrums() {
      let b = [];
      b.push({
        to: { name: "Home" },
        text: "Homepage",
        exact: true
      });
      b.push({
        to: {
          name: "Leaflets"
        },
        text: "Volantini",
        exact: true
      });
      if (this.leaflet) {
        b.push({
          to: {
            name: "Leaflet",
            params: {
              volantino: this.$route.params.volantino
            }
          },
          text: this.leaflet.title,
          exact: true
        });
      }
      if (this.showList) {
        b.push({
          to: {
            name: "LeafletList"
          },
          text: "Lista",
          exact: true
        });
      }
      return b;
    },
    dialogWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "100%";
        case "sm":
          return "80%";
        case "md":
          return "50%";
        case "lg":
          return "40%";
        case "xl":
          return "30%";
        default:
          return "auto";
      }
    },
    dialogWidthInfo() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "100%";
        case "sm":
          return "80%";
        case "md":
          return "75%";
        case "lg":
          return "66%";
        case "xl":
          return "50%";
        default:
          return "auto";
      }
    },
    isIOSSafariOrChrome() {
      const ua = window.navigator.userAgent;
      const iOS = !!ua.match(/iP(ad|hone)/i);
      const webkit = !!ua.match(/WebKit/i);
      const isChrome = !!ua.match(/Chrome/i);

      return (iOS && webkit) || (iOS && isChrome);
    }
  },
  methods: {
    setLeaflet(leaflet) {
      this.leaflet = leaflet;
    },
    handleScroll() {
      const scrollY = window.scrollY;
      const target = document.getElementById("leaflet-toolbar");

      if (scrollY > this.navOffsetTop - 120) {
        this.isSticky = true;
        target.classList.add("extended");
      } else {
        this.isSticky = false;
        target.classList.remove("extended");
      }
    },

    handleZoom() {
      let swiperInstance = this.$refs.leafletViewerRef.$refs.swiper.$swiper;
      if (this.isIOSFullscreen) {
        swiperInstance.zoom.enable();
      } else {
        swiperInstance.zoom.disable();
      }
    },

    handleShowCategory() {
      this.showCategories = !this.showCategories;
      this.isIOSFullscreen = false;
      this.handleZoom();
    },

    async toggleFullscreen() {
      const _this = this;
      if (
        this.isIOSSafariOrChrome ||
        (window.cordova && window.cordova.platformId.toLowerCase() === "ios")
      ) {
        this.isIOSFullscreen = true;
        this.handleZoom();
        this.showLeaflet = true;
      } else {
        if (_this.leafletFullscreen) {
          this.isSticky = false;
          let t = document.getElementById("leaflet-toolbar");
          t.classList.remove("extended");
          document.exitFullscreen().then(function() {
            if (_this.$vuetify.breakpoint.mdAndUp) {
              _this.swiperHeight =
                window.innerHeight - header.desktopHeader + "px";
            } else {
              _this.swiperHeight = window.innerHeight - header.smHeader + "px";
            }
          });
        } else if (document.webkitIsFullScreen == true) {
          this.isSticky = false;
          let t = document.getElementById("leaflet-toolbar");
          t.classList.remove("extended");
          document.webkitExitFullscreen().then(function() {
            if (_this.$vuetify.breakpoint.mdAndUp) {
              _this.swiperHeight =
                window.innerHeight - header.desktopHeader + "px";
            } else {
              _this.swiperHeight = window.innerHeight - header.smHeader + "px";
            }
          });
        } else {
          this.showLeaflet = true;
          let elem = this.$refs.leafletViewer;
          var methodToBeInvoked =
            elem.requestFullscreen ||
            elem.webkitRequestFullScreen ||
            elem["mozRequestFullscreen"] ||
            elem["msRequestFullscreen"];
          let _this = this;
          if (methodToBeInvoked) {
            methodToBeInvoked.call(elem).then(
              function() {
                _this.swiperHeight =
                  window.screen.height - header.fullscreenHeader + "px";
              },
              function(error) {
                console.log(error);
              }
            );
          }
        }
      }
    },

    exitFullscreen() {
      if (this.leafletFullscreen) {
        this.toggleFullscreen();
      }
    },
    exitHandler() {
      let attr =
        document.fullscreen === true ||
        document.webkitIsFullScreen === true ||
        document.mozFullScreen === true ||
        document.msFullscreenElement === true;

      if (attr === false) {
        this.leafletFullscreen = false;
      } else if (attr === true) {
        this.leafletFullscreen = true;
      }
    },

    async openDetail(areaDetail) {
      this.componentKey = this.componentKey++;
      this.exitFullscreen();
      this.selectedArea = await leafletService.searchProducts({
        parent_leaflet_id: this.leafletSlugId || this.leafletId,
        parent_leaflet_area_id: areaDetail.leafletAreaId,
        details: true
      });
      if (areaDetail) {
        if (areaDetail.link) {
          try {
            this.$emit("close");
            this.clicked(areaDetail.link);
          } catch (e) {
            this.$emit("close");
            if (areaDetail.link.indexOf("http") > -1) {
              if (this.$platform_is_cordova) {
                window.cordova.InAppBrowser.open(areaDetail.link, "_system");
              } else {
                window.open(areaDetail.link);
              }
            }
          }
          return;
        } else {
          let _this = this;
          if (
            areaDetail.metadata?.leafletareatype_cta?.IMAGE?.location ||
            (_this.selectedArea.data.products &&
              _this.selectedArea.data.products.length > 0)
          ) {
            const dialogInstance = await this.$dialog.show(CTADetailLeaflet, {
              extraClass: "cta_detail_leaflet",
              hideToolbarLogo: true,
              toolbarColor: "white",
              icon: areaDetail ? areaDetail.iconType : undefined,
              areaDetail: areaDetail,
              products: this.selectedArea.data.products,
              leaflet: this.leaflet,
              width: this.dialogWidth,
              fullscreen: this.$vuetify.breakpoint.xsOnly,
              paginationClass: `cta-leaflet-${this.componentKey}`,
              xs: 1,
              sm: 1,
              md: 1,
              lg: 1,
              xl: 1,
              showBullets: false,
              showArrows: true,
              waitForResult: false // Add this to get a controllable instance
            });

            this.dialogInstances.push(dialogInstance);
          }
        }
      }
    },

    async slideChanged(index) {
      console.log("LEAFLET slide changed:", index);

      const isDesktop = window.innerWidth > 769;
      let pageNumber = index;

      if (isDesktop && index !== 0) {
        pageNumber = [index, index + 1];
      }

      this.activeFlyerPages = pageNumber;
      await this.fetchProducts({ pageNumber, reset: true });
    },

    async fetchLeaflet() {
      if (isNaN(this.leafletId)) {
        this.leaflet = await customService.getLeafletBySlug(this.leafletId);
        if (this.leaflet) {
          this.leafletSlugId = this.leaflet.leafletId;
        }
      } else {
        this.leaflet = await leafletService.getLeafletById(this.leafletId);
      }
      await this.fetchFacets();
      this.leafletRendered = true;

      let foundCategories = this.facets?.find(
        facet => facet.name == "Categorie"
      );

      this.categories = this.allCategories.filter(aCategory => {
        let available = foundCategories?.values?.find(
          fCategory => fCategory.id == aCategory.categoryId
        );
        return available != null;
      });
    },

    async fetchProducts({ pageNumber = null, reset = false } = {}) {
      if (reset) {
        this.currentPage = 1;
        this.activeCategoryId = null;
        this.activeSearch = null;
        this.headerSearch = null;
        this.headerCategory = null;
      }

      let params = {
        parent_leaflet_id: this.leafletSlugId || this.leafletId,
        page: this.currentPage,
        page_size: this.pageSize
      };

      if (this.activeCategoryId) {
        params.parent_category_id = this.activeCategoryId;
      }

      if (this.activeSearch) {
        params.q = this.activeSearch;
      }

      if (pageNumber !== null) {
        params.parent_leaflet_page_number_id = pageNumber;
      } else if (this.activeFlyerPages !== null) {
        params.parent_leaflet_page_number_id = this.activeFlyerPages;
      }

      let response = await leafletService.searchProducts(params);

      this.products = (response.data.products || []).filter(product => {
        return product.available && product.available > 0;
      });

      this.pager = response.data.page || {
        selPage: this.currentPage,
        totPages: 1,
        totItems: this.products.length,
        itemsPerPage: this.pageSize
      };

      if (this.products.length > 0) {
        analyticsService.viewProducts(
          this.products,
          `leaflet_${this.leaflet.name || "volantino"}`,
          (this.pager.selPage - 1) * this.pager.itemsPerPage
        );
      }
    },

    async fetchFacets() {
      let response = await leafletService.searchProducts({
        parent_leaflet_id: this.leafletSlugId || this.leafletId,
        page_size: 0
      });
      this.facets = response.data.facets || [];
    },

    async onPageChange(page) {
      this.currentPage = page;
      await this.fetchProducts();
      this.$vuetify.goTo(0);
    },

    async filterProducts(categoryId, categoryName) {
      this.exitFullscreen();
      this.activeCategoryId = categoryId;
      this.activeSearch = null;
      this.headerSearch = null;
      this.headerCategory = categoryName;
      this.currentPage = 1;
      this.showLeaflet = false;
      this.activeFlyerPages = null; // remove flyer page filter when filtering by category

      await this.fetchProducts();

      this.$router.replace({
        query: { ...this.$route.query, category: categoryId }
      });

      if (this.showList) {
        this.$router.push({ name: "Leaflet" });
      }
    },

    async searchProducts() {
      this.exitFullscreen();
      this.activeSearch = this.search;
      this.activeCategoryId = null;
      this.headerCategory = null;
      this.headerSearch = this.search;
      this.currentPage = 1;
      this.showLeaflet = false;
      this.activeFlyerPages = null; // remove flyer page filter when searching

      await this.fetchProducts();

      var el = document.getElementsByClassName("flyer-input");
      if (el && el[0]) el[0].blur();
      this.isIOSFullscreen = false;
      const iosFullscreen = document.getElementById("leafet-viewer");
      iosFullscreen.classList.remove("iosFullscreen");
      this.showSearch = false;
      if (this.$refs.flyerinput) this.$refs.flyerinput.blur();

      if (this.showList) {
        this.$router.push({ name: "Leaflet" });
      }
    },

    backToLeaflet() {
      this.exitFullscreen();

      if (this.isIOSFullscreen) {
        this.isIOSFullscreen = false;
      }
      this.handleZoom();

      this.showLeaflet = true;
      this.headerSearch = null;
      this.headerCategory = null;
      this.activeCategoryId = null;
      this.activeSearch = null;
      this.currentPage = 1;
      this.products = [];
      this.pager = null;
      this.activeFlyerPages = null; // clear flyer page filter when going back

      const query = { ...this.$route.query };
      delete query.category;

      this.$router.replace({
        path: this.$route.path,
        query
      });

      if (this.showList) {
        this.$router.push({ name: "Leaflet" });
      }
    },

    onLinkClick(e) {
      var url = e.currentTarget.href;
      let fileName = "";
      this.$emit("close");
      this.$nextTick(() => {
        if (
          this.$platform_is_cordova &&
          e.currentTarget.href.endsWith(".pdf")
        ) {
          fileName = url.substring(url.lastIndexOf("/") + 1, url.length);
          fileName = fileName.substring(0, fileName.length - 4);
          this.handleReceiptDownload(url, fileName);
        } else {
          this.clicked(e);
        }
      });
    },

    onShowSearch() {
      this.showSearch = !this.showSearch;
      if (!this.showSearch) {
        var el = document.getElementsByClassName("flyer-input");
        if (el && el[0]) el[0].blur();
      }
    },

    openleafletInfoModal() {
      this.$dialog.show(LeafletInformationModalEBSN, {
        width: this.dialogWidthInfo,
        fullscreen: this.$vuetify.breakpoint.smAndDown
      });
    },

    checkPopup() {
      let _this = this;
      try {
        let popupConfig = get(_this.leaflet, "metaData.leaflet_popup");
        if (popupConfig?.ACTIVE) {
          let popupMap = global.vm.$store.getters["app/getPopupMap"];
          let canOpenPopup = false;

          if (typeof popupConfig.REOPENING_DAYS_DELAY == "number") {
            let lastTimeOpen =
              popupMap[_this.leaflet.leafletId + "" + popupConfig.TITLE];
            let today = new Date().getTime();
            if (
              !lastTimeOpen ||
              lastTimeOpen +
                popupConfig.REOPENING_DAYS_DELAY * 24 * 60 * 60 * 1000 <
                today
            ) {
              canOpenPopup = true;
            }
          } else {
            canOpenPopup = true;
          }

          if (canOpenPopup) {
            setTimeout(async function() {
              await global.vm.$dialog.show(Popup, {
                waitForResult: true,
                width: 700,
                popup: popupConfig,
                categoryId: _this.leaflet.leafletId
              });
            }, popupConfig.OPENING_DELAY * 1000);
          }
        }
      } catch (err) {
        console.log(err);
      }
    }
  },
  metaInfo() {
    return {
      title: get(this.leaflet, "title"),
      meta: [
        {
          vmid: "description",
          name: "description",
          content: get(this.leaflet, "description")
        },
        { name: "viewport", content: "width=device-width, user-scalable=no" }
      ]
    };
  },
  created() {
    global.EventBus.$on("showLeafletInfo", () => {
      this.openleafletInfoModal();
    });
  },
  async mounted() {
    this.leafletId = this.$route.params.leafletId;

    await this.fetchLeaflet();

    let page = Math.max(
      1,
      Number(this.$route.params.page || this.$route.query.page) || 1
    );
    const isDesktop = window.innerWidth > 769;

    let pagesToFetch;
    if (!isDesktop) {
      pagesToFetch = page - 1;
    } else if (page === 1) {
      pagesToFetch = [0];
    } else {
      pagesToFetch = [page - (page % 2), page - (page % 2) + 1];
    }

    this.activeFlyerPages = pagesToFetch;
    await this.fetchProducts({ pageNumber: pagesToFetch });

    this.checkPopup();

    if (this.$route.name === "LeafletList") {
      this.showList = true;
    } else {
      this.showList = false;
    }

    let elem = this.$refs.leafletViewer;
    elem.addEventListener("fullscreenchange", this.exitHandler, false);
    elem.addEventListener("mozfullscreenchange", this.exitHandler, false);
    elem.addEventListener("MSFullscreenChange", this.exitHandler, false);
    elem.addEventListener("webkitfullscreenchange", this.exitHandler, false);

    global.EventBus.$on("openDetail", areaDetail => {
      this.openDetail(areaDetail);
    });

    global.EventBus.$on("slideChanged", index => {
      this.slideChanged(index);
    });

    if (this.$vuetify.breakpoint.smAndUp) {
      this.$nextTick(() => {
        const nav = document.getElementById("leaflet-toolbar");
        if (nav) {
          this.navOffsetTop =
            nav.getBoundingClientRect().bottom + window.scrollY;
        }
        window.addEventListener("scroll", this.handleScroll);
      });
    } else {
      this.isSticky = false;
      let t = document.getElementById("leaflet-toolbar");
      t.classList.remove("extended");
    }

    const destCategory = this.$route.query?.category
      ? Number(this.$route.query.category)
      : null;
    const categoriesIds = this.categories.map(c => c.categoryId);
    if (destCategory && categoriesIds.includes(destCategory)) {
      const category = this.categories.find(c => c.categoryId === destCategory);
      if (category) {
        await this.filterProducts(destCategory, category.name);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    let elem = this.$refs.leafletViewer;
    if (elem) {
      elem.removeEventListener("fullscreenchange", this.exitHandler);
      elem.removeEventListener("mozfullscreenchange", this.exitHandler);
      elem.removeEventListener("MSFullscreenChange", this.exitHandler);
      elem.removeEventListener("webkitfullscreenchange", this.exitHandler);
    }
    this.dialogInstances.forEach(instance => {
      if (instance && typeof instance.close === "function") {
        instance.close();
      }
    });
    // Clear the array after closing (optional, but good cleanup)
    this.dialogInstances = [];
  },
  watch: {
    "$route.params.leafletId": async function() {
      if (isNumber(this.$route.params.leafletId)) {
        this.leafletId = this.$route.params.leafletId;
      } else {
        this.leafletId = this.$route.params.leafletId;
      }
      await this.fetchLeaflet();
      await this.fetchProducts();
    },
    "$vuetify.breakpoint.smAndUp"(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const nav = document.getElementById("leaflet-toolbar");
          if (nav) {
            this.navOffsetTop =
              nav.getBoundingClientRect().bottom + window.scrollY;
          }
          window.addEventListener("scroll", this.handleScroll);
        });
      } else {
        this.isSticky = false;
        const t = document.getElementById("leaflet-toolbar");
        if (t) t.classList.remove("extended");
        window.removeEventListener("scroll", this.handleScroll);
      }
    },
    isIOSFullscreen(newValue) {
      if (newValue) {
        document.body.classList.add("body-fixed-position");
      } else {
        document.body.classList.remove("body-fixed-position");
      }
    }
  }
};
</script>
